/**
 * FolderPanel — a tight CRUD panel over a /documents/<subdir>/ folder.
 *
 * Powers the 备课笔记 (/documents/notes/) and 生成产物
 * (/documents/artifacts/) sections on NotesFilesPage. Lists files,
 * uploads new ones, view (download with original filename) and delete
 * per row; markdown files additionally get an inline Edit that pops a
 * textarea modal and re-uploads on Save. The Promote-to-org button is
 * rendered per row when promoteEnabled is true, so artifacts can be
 * submitted into the school knowledge base via the existing
 * promotion-svc flow.
 *
 * Storage uses the documentsApi helpers already proven on the teaching-
 * plans page (Nova OS /api/documents/{tree,upload,download,rm}/), so
 * this stays additive to that surface.
 */
import { searchableTwinOf } from "../../lib/searchableTwin";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";
import {
  deleteDoc,
  formatFileTime,
  listDocs,
  readDocText,
  sortFilesByTimeDesc,
  subjectFilesApi,
  uploadDoc,
} from "../teachingPlans/documentsApi";
import { FilePreviewModal } from "./FilePreviewModal";

// Extensions supernova's parser actually ingests (internal/docs/parser.go
// DetectType): pdf/docx/xlsx/md/markdown/txt/csv/json/xml/yaml/html/eml/odt.
// Notably NOT: .doc (legacy OLE), .pptx/.ppt, .ods/.odp, images, media.
const KB_SEARCHABLE_EXT =
  /\.(pdf|docx|xlsx|md|markdown|txt|csv|json|xml|ya?ml|html?|eml|odt)$/i;

const INGEST_GRACE_MS = 20 * 60 * 1000;

/** 上传时间在 20 分钟内 → 大文件还可能在分块/嵌入,别急着判"未入索引"。 */
export function isRecentlyUploaded(createdAt?: string | null, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  return Number.isFinite(t) && now - t < INGEST_GRACE_MS;
}

function kbBadgeFor(
  subdir: string,
  name: string,
  status?: string | null,
  createdAt?: string | null,
): { labelKey: string; hintKey: string; green: boolean } {
  if (subdir === "artifacts" || subdir.startsWith("artifacts/")) {
    return {
      labelKey: "kbArtifactExcluded",
      hintKey: "kbArtifactExcludedHint",
      green: false,
    };
  }
  // Server-confirmed ingestion (tree API status, backed by actual chunk
  // sources) beats the extension heuristic.
  if (status === "ingested") {
    return { labelKey: "kbIngested", hintKey: "kbIngestedHint", green: true };
  }
  // 索引没了但文件还在 —— 必须说实话。
  //
  // 这里曾经直接掉进下面的扩展名判断,于是 .md/.pdf 不管有没有入索引都
  // 显示绿色「可检索」。2026-08-29 有人在 OS 后台把 15 个知识集合全删了,
  // 学科管理页看起来一切正常(文件都在、徽标全绿),实际上 AI 一个字都
  // 检索不到 —— 直到去聊天里问才发现。徽标在说谎。
  //
  // status 为 null(字段在、值为空)= 服务端明确说"没入索引";
  // status 为 undefined(旧版 nova-os 不返回这个字段)= 无从判断,
  // 沿用扩展名启发,不误报。
  // 刚上传、还在分块/嵌入的文件也是 status === null。2026-09-03 Jarod 传了
  // 4 个共 1 MB 的 md,一分钟后截图全是「未入索引」+「重建索引」——几分钟后
  // 450 个 chunk 全进去了。徽标把"入库中"说成了"没入库"。上传后 20 分钟内的
  // null 按"入库中"显示、不给重建按钮;过了窗口还是 null 才是真的没入索引。
  if (status === null && KB_SEARCHABLE_EXT.test(name) && isRecentlyUploaded(createdAt)) {
    return { labelKey: "kbIngesting", hintKey: "kbIngestingHint", green: false };
  }
  if (status === null && KB_SEARCHABLE_EXT.test(name)) {
    return { labelKey: "kbNotIndexed", hintKey: "kbNotIndexedHint", green: false };
  }
  if (KB_SEARCHABLE_EXT.test(name)) {
    return { labelKey: "kbSearchable", hintKey: "kbSearchableHint", green: true };
  }
  return {
    labelKey: "kbNotSearchable",
    hintKey: "kbNotSearchableHint",
    green: false,
  };
}

interface Props {
  /** Subdir under /documents/, e.g. "notes" or "artifacts". */
  subdir: string;
  /** Human-friendly heading shown on the panel. */
  heading: string;
  /** Optional one-line hint under the heading. */
  hint?: string;
  /** Show the Submit-for-approval (Promote to org) button per row. */
  promoteEnabled?: boolean;
  /** 知识集合名(如 subject_math)。不传则按 nova-os 默认归集
   *  (管理员→default,老师→user_<id>)。学科页必须传。 */
  collection?: string;
  /** 学科 slug。传了就改走 school-backend 的学科文件代理
   *  (/api/school/subjects/<slug>/files)而不是直连 nova-os。
   *
   *  为什么必须绕这一道:园长的 nova 角色是 manager,直连会被路径重写 +
   *  CanWrite 挡住 —— 列表 0 条、上传 403、删除 404,学科管理页对她完全
   *  不可用。代理端先校验园长/管理员身份,再以服务身份执行。 */
  subjectSlug?: string;
}

export function FolderPanel({
  subdir,
  heading,
  hint,
  promoteEnabled = false,
  collection,
  subjectSlug,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const tree = useQuery({
    queryKey: ["documents-tree", subdir],
    queryFn: () => (subjectSlug ? subjectFilesApi.list(subjectSlug) : listDocs(subdir)),
    retry: false,
    // 有"入库中"的文件就每 15 s 刷一次,徽标自己从灰变绿,不用用户手动刷新。
    refetchInterval: (q) => {
      const files =
        (q.state.data as { files?: Array<{ status?: string | null; created_at?: string | null }> } | undefined)?.files ?? [];
      return files.some((f) => f.status === null && isRecentlyUploaded(f.created_at)) ? 15_000 : false;
    },
  });

  const upload = useMutation({
    // Batch upload: the file input is `multiple`, so uploads run
    // sequentially and the panel refreshes once at the end.
    mutationFn: async (fs: File[]) => {
      for (const f of fs) {
        if (subjectSlug) await subjectFilesApi.upload(subjectSlug, f.name, f);
        else await uploadDoc(subdir, f.name, f, collection);
        // ppt/doc 等不可检索格式 → 生成 PDF 孪生一起入库(可检索)
        const twin = await searchableTwinOf(f);
        if (twin) {
          if (subjectSlug) await subjectFilesApi.upload(subjectSlug, twin.name, twin);
          else await uploadDoc(subdir, twin.name, twin, collection);
        }
      }
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-tree", subdir] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
    },
  });

  const remove = useMutation({
    // Accepts one or many names so a single row delete and a multi-select
    // delete share one code path.
    mutationFn: async (names: string[]) => {
      for (const n of names) {
        if (subjectSlug) await subjectFilesApi.remove(subjectSlug, n);
        else await deleteDoc(`${subdir}/${n}`);
      }
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-tree", subdir] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
      setSelected(new Set());
    },
  });

  function pickFile() {
    inputRef.current?.click();
  }
  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const list = Array.from(e.target.files ?? []);
    if (!list.length) return;
    upload.mutate(list);
    e.target.value = "";
  }

  function toggleSelect(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function askDelete(name: string) {
    const ok = window.confirm(
      t("documents.files.deleteConfirmFile", { name }),
    );
    if (!ok) return;
    remove.mutate([name]);
  }

  function askDeleteSelected() {
    const names = [...selected];
    if (!names.length) return;
    const ok = window.confirm(
      t("documents.files.deleteConfirmMany", { count: names.length }),
    );
    if (!ok) return;
    remove.mutate(names);
  }

  // Newest first — generated artifacts pile up fast, and the latest one is
  // almost always the one the teacher wants (2026-07-27 ask).
  const files = sortFilesByTimeDesc(tree.data?.files ?? []);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">{heading}</h2>
          {hint && (
            <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              type="button"
              onClick={askDeleteSelected}
              disabled={remove.isPending}
              className="px-3 py-1 text-xs font-medium rounded border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              {remove.isPending
                ? t("common.deleting")
                : t("documents.files.deleteSelected", { count: selected.size })}
            </button>
          )}
          <button
            type="button"
            onClick={pickFile}
            disabled={upload.isPending}
            className="px-3 py-1 text-xs font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {upload.isPending
              ? t("documents.files.uploading")
              : t("documents.files.uploadButton")}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onFileChange}
          />
        </div>
      </header>

      {error && (
        <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      {tree.isLoading ? (
        <p className="text-xs text-slate-500 italic">{t("documents.files.loading")}</p>
      ) : tree.error ? (
        <p className="text-xs text-rose-600">
          {t("documents.files.loadError", { msg: (tree.error as Error).message })}
        </p>
      ) : files.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          {t("documents.files.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {files.map((f) => {
            const isMarkdown = /\.(md|markdown)$/i.test(f.name);
            return (
              <li
                key={f.name}
                className="group flex items-center gap-3 py-2 text-xs"
              >
                <input
                  type="checkbox"
                  checked={selected.has(f.name)}
                  onChange={() => toggleSelect(f.name)}
                  className="h-3.5 w-3.5 shrink-0 accent-sky-500"
                  aria-label={f.name}
                />
                <span className="text-base">📄</span>
                <span className="flex-1 truncate text-slate-800">{f.name}</span>
                {/* Personal-KB status badge. Three states, mirroring the
                    server's actual ingestion rules: artifacts are excluded
                    by design (self-pollution guard, nova-os#780); other
                    files are searchable iff supernova's parser handles the
                    format. Real per-file ingestion status (doc_id) is not
                    populated by the tree API yet — P1. */}
                {(() => {
                  const badge = kbBadgeFor(subdir, f.name, f.status, f.created_at);
                  return (
                    <span
                      title={String(t(`documents.files.${badge.hintKey}`))}
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap ${badge.green ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
                    >
                      {t(`documents.files.${badge.labelKey}`)}
                    </span>
                  );
                })()}
                {f.created_at && (
                  <span className="shrink-0 text-[10px] text-slate-400 whitespace-nowrap">
                    {formatFileTime(f.created_at)}
                  </span>
                )}
                {f.status === null && KB_SEARCHABLE_EXT.test(f.name) && !isRecentlyUploaded(f.created_at) && (
                  <ReindexFileButton
                    subdir={subdir}
                    filename={f.name}
                    collection={collection}
                    subjectSlug={subjectSlug}
                  />
                )}
                {promoteEnabled && (
                  <PromoteFileButton subdir={subdir} filename={f.name} />
                )}
                {isMarkdown && (
                  <button
                    type="button"
                    onClick={() => setEditing(f.name)}
                    className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-sky-700 hover:bg-sky-50 transition-opacity"
                  >
                    {t("common.edit")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPreview(f.name)}
                  className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-slate-700 hover:bg-slate-100 transition-opacity"
                >
                  {t("common.view")}
                </button>
                <button
                  type="button"
                  onClick={() => askDelete(f.name)}
                  disabled={remove.isPending}
                  className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-opacity"
                >
                  {t("documents.files.delete")}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <EditMarkdownModal
          collection={collection}
          subjectSlug={subjectSlug}
          subdir={subdir}
          filename={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["documents-tree", subdir] });
          }}
        />
      )}

      {preview && (
        <FilePreviewModal
          path={`${subdir}/${preview}`}
          filename={preview}
          downloadUrl={
            subjectSlug ? subjectFilesApi.downloadUrl(subjectSlug, preview) : undefined
          }
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

// ── Per-row Promote button — opens the existing PromoteToOrgButton
//    modal pre-seeded with this file's source path. Exported so the
//    legacy FilesPanel (the "我的文件" surface in NotesFilesPage) can
//    reuse the same modal + backend submit-promotion proxy. ─────────

export function PromoteFileButton({
  subdir,
  filename,
}: {
  subdir: string;
  filename: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-amber-700 hover:bg-amber-50 transition-opacity"
        title={t("documents.promote.tooltip")}
      >
        {t("documents.promote.action")}
      </button>
      {open && (
        <PromoteModal
          source={`personal:${subdir ? `${subdir}/${filename}` : filename}`}
          filename={filename}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function inferArtifactType(source: string, filename: string): string {
  if (source.includes("assignments")) return "assignment_doc";
  if (source.includes("teaching-plans")) return "teaching_plan";
  if (source.includes("artifacts")) {
    if (/\.pptx$/i.test(filename)) return "courseware";
    return "chat_doc";
  }
  if (/\.(md|txt)$/i.test(filename)) return "note";
  return "file";
}

function PromoteModal({
  source,
  filename,
  onClose,
}: {
  source: string;
  filename: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  // Subjects come from the school-backend's admin_subjects router
  // (we i18n'd + auth-fixed it in #142). Reuse the same endpoint.
  const subjects = useQuery({
    queryKey: ["admin-subjects-for-promote"],
    queryFn: async () => {
      const r = await fetch("/api/admin/subjects", { headers: authHeaders() });
      if (!r.ok) throw new Error(`subjects ${r.status}`);
      return (await r.json()) as { id: string; slug: string; name: string }[];
    },
    retry: false,
  });

  // Default-select the first subject once the list arrives, so the
  // admin doesn't need an extra click to set the target. Only fires
  // once per modal — if the admin manually picks a different option,
  // their choice sticks because target is no longer empty.
  useEffect(() => {
    if (target !== "") return;
    const first = subjects.data?.[0];
    if (first) {
      setTarget(`subject_${first.slug}`);
    }
  }, [subjects.data, target]);

  async function submit(personalOnly: boolean) {
    if (!target && !personalOnly) return;
    setBusy(true);
    setErr(null);
    try {
      const slug = target.replace(/^subject_/, "");
      const subjectId = subjects.data?.find((s2) => s2.slug === slug)?.id;
      const r = await fetch("/api/school/promotions/submit", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          source_collection: source,
          target_collection: personalOnly ? "" : target,
          artifact_type: inferArtifactType(source, filename),
          ...(subjectId && !personalOnly ? { source_subject_id: subjectId } : {}),
          ...(personalOnly ? { personal_only: true } : {}),
        }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        detail?: string; personal_ingest_status?: string;
      };
      if (!r.ok) {
        throw new Error(typeof body.detail === "string" ? body.detail : `HTTP ${r.status}`);
      }
      if (body.personal_ingest_status === "failed") {
        setDoneMsg(t("documents.promote.ingestFailed"));
        setBusy(false);
        return;
      }
      setDoneMsg(personalOnly ? t("documents.promote.personalDone") : t("documents.promote.bothDone"));
      setBusy(false);
      setTimeout(onClose, 1600);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-[min(440px,calc(100vw-32px))] rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">
            {t("documents.promote.modalTitle")}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">×</button>
        </div>
        <div className="text-xs text-slate-600 mb-3">
          {t("documents.promote.sourceLabel")} <code className="text-sky-700 break-all">{filename}</code>
        </div>
        <label className="block text-xs text-slate-600 mb-1">
          {t("documents.promote.targetLabel")}
        </label>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm mb-3"
          autoFocus
        >
          {/* Sentinel only renders while the subjects query is still
              loading; once subjects arrive the useEffect above
              defaults target to the first slug, which moves selection
              off the sentinel. Keeping it as a fallback prevents the
              select from appearing empty mid-load. */}
          {target === "" && (
            <option value="">{t("documents.promote.pickTarget")}</option>
          )}
          {(subjects.data ?? []).map((s) => (
            <option key={s.id} value={`subject_${s.slug}`}>
              {s.name} (subject_{s.slug})
            </option>
          ))}
        </select>
        {subjects.data && subjects.data.length === 0 && (
          <p className="text-[11px] text-slate-500 mb-3">
            {t("documents.promote.noSubjects")}
          </p>
        )}
        {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
        {doneMsg && <p className="text-xs text-emerald-600 mb-2">{doneMsg}</p>}
        <div className="flex gap-2 justify-end items-center">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1 rounded hover:bg-slate-100"
          >
            {t("common.cancel")}
          </button>
          {/* 次级动作:仅入个人库(§3.4,保护审批队列信噪比) */}
          <button
            type="button"
            onClick={() => void submit(true)}
            disabled={busy}
            className="text-xs px-3 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
          >
            {t("documents.promote.personalOnly")}
          </button>
          <button
            type="button"
            onClick={() => void submit(false)}
            disabled={busy || !target}
            className="text-xs px-3 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {busy ? t("documents.promote.submitting") : t("documents.promote.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditMarkdownModal({
  subdir,
  filename,
  collection,
  subjectSlug,
  onClose,
  onSaved,
}: {
  subdir: string;
  filename: string;
  collection?: string;
  subjectSlug?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load initial body once when the modal opens.
  useQuery({
    queryKey: ["doc-text", `${subdir}/${filename}`],
    queryFn: async () => {
      // Subject files read through the proxy for the same reason preview does —
      // a direct Nova OS path 404s for a non-admin caller (scope.go RewritePath).
      const text = subjectSlug
        ? await fetch(subjectFilesApi.downloadUrl(subjectSlug, filename), {
            headers: authHeaders(),
          }).then((r) => {
            if (!r.ok) throw new Error(`download ${r.status}`);
            return r.text();
          })
        : await readDocText(`${subdir}/${filename}`);
      setDraft(text);
      return text;
    },
    retry: false,
  });

  async function save() {
    if (draft === null) return;
    setBusy(true);
    setErr(null);
    try {
      const body = new Blob([draft], { type: "text/markdown" });
      if (subjectSlug) await subjectFilesApi.upload(subjectSlug, filename, body);
      else await uploadDoc(subdir, filename, body, collection);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-[min(720px,calc(100vw-32px))] max-h-[80vh] flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {t("common.edit")}
            </h3>
            <p className="text-[11px] text-slate-500 font-mono">{filename}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">×</button>
        </header>
        {draft === null ? (
          <p className="text-xs text-slate-500 italic">{t("common.loading")}</p>
        ) : (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 min-h-[300px] w-full px-3 py-2 rounded border border-slate-200 font-mono text-xs resize-y"
          />
        )}
        {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
        <div className="flex gap-2 justify-end mt-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1 rounded hover:bg-slate-100"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || draft === null}
            className="text-xs px-3 py-1 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 重建索引 —— 文件在、索引没了时的自助恢复。
 *
 *  为什么需要它:nova-os 没有 reindex 接口(我查过,只有查进度的
 *  /api/knowledge/ingest/progress)。索引只在上传那一刻写一次,之后文件
 *  静静躺在存储里,不会自动重新索引。所以一旦集合被删(2026-08-29 就有人
 *  在 OS 后台把 15 个集合全删了),数据明明没丢却无法自助恢复,只能找人
 *  按文件重传。
 *
 *  这个按钮做的就是等价的事:把文件原样读回来,再带正确的 collection
 *  传一次 —— 触发重新切块入索引。同名同路径覆盖,所以是幂等的。
 *
 *  只在「文件在 + 服务端说没入索引 + 格式可解析」时出现,平时不打扰。 */
function ReindexFileButton({
  subdir,
  filename,
  collection,
  subjectSlug,
}: {
  subdir: string;
  filename: string;
  collection?: string;
  subjectSlug?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function reindex() {
    setBusy(true);
    setErr(null);
    try {
      // 学科目录必须走 school-backend 代理。直连 nova-os 对园长四样全不通
      // (路径被重写到 users/<id>/、写权限只认个人库),表现就是这个按钮
      // 变红而没有任何说明(Jarod 2026-09-02)。上传那条链路早就是学科感知
      // 的,只有这里漏了。
      let blob: Blob;
      if (subjectSlug) {
        const r = await fetch(subjectFilesApi.downloadUrl(subjectSlug, filename), {
          headers: authHeaders(),
        });
        if (!r.ok) throw new Error(`下载失败 (${r.status})`);
        blob = await r.blob();
      } else {
        const path = `${subdir}/${filename}`.replace(/^\/+/, "");
        const r = await fetch(
          `/api/documents/download/${path.split("/").map(encodeURIComponent).join("/")}`,
          { headers: authHeaders() },
        );
        if (!r.ok) throw new Error(`下载失败 (${r.status})`);
        blob = await r.blob();
      }
      if (blob.size === 0) throw new Error("文件是空的");
      if (subjectSlug) await subjectFilesApi.upload(subjectSlug, filename, blob);
      else await uploadDoc(subdir, filename, blob, collection);
      await qc.invalidateQueries({ queryKey: ["documents-tree", subdir] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // 失败信息以前只写进 title 气泡 —— 用户看到的就是"按钮变红、其他没变化"。
  // 现在直接显示在旁边。
  return (
    <span className="shrink-0 inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => void reindex()}
        disabled={busy}
        title={String(t("documents.files.reindexHint"))}
        className={`text-xs px-2 py-0.5 rounded disabled:opacity-40 ${
          err ? "text-rose-600 hover:bg-rose-50" : "text-amber-700 hover:bg-amber-50"
        }`}
      >
        {busy ? t("documents.files.reindexing") : t("documents.files.reindex")}
      </button>
      {err && <span className="text-[10px] text-rose-600 max-w-[14rem] truncate">{err}</span>}
    </span>
  );
}
